import { ArrowLeft, UserX } from 'lucide-react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/card';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/avatar';
import { ScrollArea } from '../../components/scroll-area';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../../components/table';
import type { StaffRecord } from './StaffTable';

export type StaffActivityRecord = {
    id: string;
    created_at: string;
    location?: string;
    state?: string;
    country?: string;
    ip_address: string;
};

export type StaffOrderRecord = {
    id: string;
    order_id: string;
    job_title: string;
    product_type: string;
    status: string;
    size?: string;
    order_type?: string;
};

export type StaffDetailsViewProps = {
    icon: string;
    staff: StaffRecord;
    activity: StaffActivityRecord[] | undefined;
    orders?: StaffOrderRecord[];
    showOrders?: boolean;
    canManage: boolean;
    onBack: () => void;
    onToggleActive: () => void;
};

export default function StaffDetailsView({
    icon,
    staff,
    activity,
    orders,
    showOrders,
    canManage,
    onBack,
    onToggleActive,
}: StaffDetailsViewProps) {
    return (
        <div className="p-[24px]">
            <div className="flex justify-between items-center px-4 py-3 bg-white border border-[#E9EAEB] rounded-[8px]">
                <div className="flex items-center gap-2">
                    <div
                        className="border rounded-[4px] p-1 cursor-pointer hover:bg-gray-100 transition-colors"
                        onClick={onBack}
                    >
                        <ArrowLeft size={12} strokeWidth={1} />
                    </div>
                    <p className="text-body2 font-semibold text-gray-800 capitalize">
                        {staff.full_name}
                    </p>
                </div>
                {canManage && (
                    staff.is_active ? (
                        <div
                            onClick={onToggleActive}
                            className="flex items-center gap-2 text-[#B00020] caption border border-[#E9EAEB] rounded-[4px] p-[4px] cursor-pointer"
                        >
                            <UserX size={14} /> <p>Deactivate Account</p>
                        </div>
                    ) : (
                        <div
                            onClick={onToggleActive}
                            className="flex items-center gap-2 text-[#005C00] caption border border-[#E9EAEB] rounded-[4px] p-[4px] cursor-pointer"
                        >
                            <UserX size={14} /> <p>Activate Account</p>
                        </div>
                    )
                )}
            </div>

            <div className={`grid mt-[20px] gap-[20px] ${showOrders ? 'grid-cols-2' : 'grid-cols-1'}`}>
                <div>
                    <Card>
                        <CardHeader>
                            <CardTitle>
                                <p className="flex items-center gap-[8px] body2">
                                    <img src={icon} alt="Create Order" />
                                    Staff details
                                </p>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="py-[12px]">
                            <div className="flex items-center gap-[8px]">
                                <Avatar className="w-[42px] h-[42px]">
                                    <AvatarImage src={staff.profile_pic} alt={staff.full_name} />
                                    <AvatarFallback className="bg-gray-200 text-[#202020] text-sm">
                                        {staff.full_name[0]}
                                    </AvatarFallback>
                                </Avatar>
                                <div>
                                    <p className="body1 capitalize">{staff.full_name}</p>
                                    <p className="capitalize">{staff.job_title}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-2 mt-[26px]">
                                <p>Added On</p>
                                <p className="font-bold">
                                    {format(new Date(staff.created_at), 'MMMM, dd yyyy')}
                                </p>
                            </div>
                            <div className="grid grid-cols-2 mt-[16px]">
                                <p>Status</p>
                                <p className="font-bold">{staff.is_active ? 'Active' : 'Inactive'}</p>
                            </div>
                            <div className="grid grid-cols-2 mt-[16px]">
                                <p>Email Address</p>
                                <p className="font-bold">{staff.email}</p>
                            </div>
                            <div className="grid grid-cols-2 mt-[16px]">
                                <p>Phone Number</p>
                                <p className="font-bold">{staff.phone_number}</p>
                            </div>
                        </CardContent>
                    </Card>

                    <Card className="mt-[20px] gap-0">
                        <CardHeader>
                            <CardTitle>
                                <p className="flex items-center gap-[8px] body2">
                                    <img src={icon} alt="Create Order" />
                                    Recent access
                                </p>
                            </CardTitle>
                        </CardHeader>
                        <CardContent className="p-0">
                            <ScrollArea className="max-h-[300px] overflow-y-auto">
                                <Table className="w-full text-sm text-left border-collapse">
                                    <TableHeader>
                                        <TableRow className="text-muted-foreground border-b body2 !font-semibold !text-[#534D59]">
                                            <TableHead className="py-4 px-5">Date</TableHead>
                                            <TableHead className="py-4 px-5">Location</TableHead>
                                            <TableHead className="py-4 px-5">IP address</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {activity?.length ? (
                                            activity.map((log) => (
                                                <TableRow
                                                    key={log.id}
                                                    className="border-b hover:bg-muted/50 body2"
                                                >
                                                    <TableCell className="py-4 px-5">
                                                        {format(new Date(log.created_at), 'dd MMM yyyy')}
                                                    </TableCell>
                                                    <TableCell className="py-4 px-5">
                                                        {log.location && `${log.location}`} {log.state && `${log.state},`}{' '}
                                                        {log.country && `${log.country}`}
                                                    </TableCell>
                                                    <TableCell className="py-4 px-5">{log.ip_address}</TableCell>
                                                </TableRow>
                                            ))
                                        ) : (
                                            <TableRow>
                                                <TableCell
                                                    colSpan={3}
                                                    className="text-center py-6 text-gray-500 italic"
                                                >
                                                    No recent activity found.
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </ScrollArea>
                        </CardContent>
                    </Card>
                </div>

                {showOrders && (
                    <div>
                        <Card className="gap-0">
                            <CardHeader>
                                <CardTitle>
                                    <p className="flex items-center gap-[8px] body2">
                                        <img src={icon} alt="Create Order" />
                                        Orders under {staff.full_name}
                                    </p>
                                </CardTitle>
                            </CardHeader>
                            <CardContent className="p-0">
                                <ScrollArea className="max-h-[100vh]">
                                    <div className="overflow-x-auto w-full">
                                        <Table className="min-w-[70vw] text-sm text-left border-collapse">
                                            <TableHeader>
                                                <TableRow className="text-muted-foreground border-b body2 !font-semibold !text-[#534D59]">
                                                    <TableHead className="px-5 py-4">Order ID</TableHead>
                                                    <TableHead className="px-5 py-4">Job Title</TableHead>
                                                    <TableHead className="px-5 py-4">Type of Jewelry</TableHead>
                                                    <TableHead className="px-5 py-4">Status</TableHead>
                                                    <TableHead className="px-5 py-4">Size</TableHead>
                                                    <TableHead className="px-5 py-4">Order Type</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {orders?.length ? (
                                                    orders.map((order) => (
                                                        <TableRow
                                                            key={order.id}
                                                            className="border-b hover:bg-muted/50 body2"
                                                        >
                                                            <TableCell className="px-5 py-4">{order.order_id}</TableCell>
                                                            <TableCell className="px-5 py-4">{order.job_title}</TableCell>
                                                            <TableCell className="px-5 py-4">{order.product_type}</TableCell>
                                                            <TableCell className="px-5 py-4">{order.status}</TableCell>
                                                            <TableCell className="px-5 py-4">{order.size}</TableCell>
                                                            <TableCell className="px-5 py-4">{order.order_type}</TableCell>
                                                        </TableRow>
                                                    ))
                                                ) : (
                                                    <TableRow>
                                                        <TableCell
                                                            colSpan={6}
                                                            className="text-center py-6 text-gray-500 italic"
                                                        >
                                                            No orders found under this staff member.
                                                        </TableCell>
                                                    </TableRow>
                                                )}
                                            </TableBody>
                                        </Table>
                                    </div>
                                </ScrollArea>
                            </CardContent>
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
}
